import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowUpRight,
  Download,
  Loader2,
  LockKeyhole,
  Mail,
  RefreshCw,
  LogOut,
} from "lucide-react";
import ContentManager from "../components/admin/ContentManager";
import ClientsManager from "../components/admin/ClientsManager";
import PipelineStats from "../components/admin/PipelineStats";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "tinkershift.admin.key";

const TABS = [
  { key: "service_enquiries", label: "Service Enquiries", color: "#FF007F" },
  { key: "leads", label: "Discovery Leads", color: "#0055FF" },
  { key: "checklist_submissions", label: "Checklists", color: "#FF6600" },
  { key: "early_access", label: "Early Access", color: "#00C2B8" },
  { key: "site_content", label: "Site Content", color: "#1A1A1A" },
  { key: "clients", label: "Clients", color: "#4A4A4A" },
];

const PIPELINE_TABS = new Set(["service_enquiries", "leads"]);

const STATUSES = [
  { key: "new", label: "New", color: "#FFD700", darkText: true },
  { key: "contacted", label: "Contacted", color: "#0055FF" },
  { key: "won", label: "Won", color: "#00C2B8" },
  { key: "lost", label: "Lost", color: "#9CA3AF" },
];

const SERVICE_LABELS = {
  strategy: "Makerspace Strategy & Consulting",
  "professional-development": "Educator Professional Development",
  launch: "Makerspace Launch & Activation",
  platform: "TinkerShift Platform",
  resources: "Curriculum, Resources & Maker Kits",
};

function mailtoFor(tab, r) {
  if (!r?.email) return null;
  const firstName = (r.full_name || r.name || "").split(" ")[0] || "there";
  const date = r.created_at
    ? new Date(r.created_at).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" })
    : "";
  let context, original;
  if (tab === "service_enquiries") {
    context = SERVICE_LABELS[r.service] || "your enquiry";
    original = r.requirements;
  } else if (tab === "leads") {
    context = `your interest in ${r.interest || "TinkerShift"}`;
    original = r.message;
  } else if (tab === "checklist_submissions") {
    context = "your makerspace inventory checklist";
    original = [
      `Already have (${(r.have_items || []).length}): ${(r.have_items || []).join(", ") || "—"}`,
      `Missing (${(r.missing_items || []).length}): ${(r.missing_items || []).join(", ") || "—"}`,
      r.notes ? `Notes: ${r.notes}` : null,
    ].filter(Boolean).join("\n");
  } else {
    context = "joining the TinkerShift Platform early-access list";
    original = "";
  }
  const subject = `Re: Your TinkerShift enquiry — ${context}`;
  const quoted = original
    ? `\n\n--- Original message from ${r.organisation || "you"}${date ? ` (${date})` : ""} ---\n${original}`
    : "";
  const body = `Hi ${firstName},\n\nThanks for reaching out to TinkerShift about ${context}.\n\n\n\nBest,\nJulija\nTinkerShift by TinkerTank${quoted}`;
  return `mailto:${r.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

function toCSV(rows) {
  if (!rows?.length) return "";
  const cols = Array.from(
    rows.reduce((set, r) => {
      Object.keys(r).forEach((k) => set.add(k));
      return set;
    }, new Set()),
  );
  const escape = (v) => {
    if (v == null) return "";
    const s = typeof v === "object" ? JSON.stringify(v) : String(v);
    return `"${s.replace(/"/g, '""')}"`;
  };
  return [
    cols.join(","),
    ...rows.map((r) => cols.map((c) => escape(r[c])).join(",")),
  ].join("\n");
}

function downloadCSV(filename, csv) {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

export default function AdminPage() {
  const [key, setKey] = useState(() => localStorage.getItem(STORAGE_KEY) || "");
  const [authed, setAuthed] = useState(() => !!localStorage.getItem(STORAGE_KEY));
  const [data, setData] = useState(null);
  const [siteContent, setSiteContent] = useState(null);
  const [clients, setClients] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("service_enquiries");
  const [statusBusy, setStatusBusy] = useState(null);

  const loadSiteContent = async () => {
    try {
      const { data } = await axios.get(`${API}/site-content`);
      setSiteContent(data);
    } catch {
      /* strip falls back to bundled content */
    }
  };

  const loadClients = async (k) => {
    try {
      const { data } = await axios.get(`${API}/admin/clients`, {
        headers: { "X-Admin-Key": k },
      });
      setClients(data);
    } catch {
      /* listed as empty — non-fatal */
    }
  };

  const load = async (k) => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/dump`, {
        headers: { "X-Admin-Key": k },
      });
      setData(data);
      setAuthed(true);
      localStorage.setItem(STORAGE_KEY, k);
      loadSiteContent();
      loadClients(k);
    } catch {
      toast.error("Invalid admin key.");
      setAuthed(false);
      localStorage.removeItem(STORAGE_KEY);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (collection, id, status) => {
    setStatusBusy(`${collection}:${id}`);
    try {
      await axios.patch(
        `${API}/admin/${collection}/${id}/status`,
        { status },
        { headers: { "X-Admin-Key": key } },
      );
      setData((prev) => ({
        ...prev,
        [collection]: prev[collection].map((r) =>
          r.id === id ? { ...r, status } : r,
        ),
      }));
      toast.success(`Marked as ${status}.`);
    } catch {
      toast.error("Could not update status.");
    } finally {
      setStatusBusy(null);
    }
  };

  useEffect(() => {
    if (authed && key) load(key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const signOut = () => {
    localStorage.removeItem(STORAGE_KEY);
    setAuthed(false);
    setData(null);
    setKey("");
  };

  const rows = useMemo(() => data?.[tab] || [], [data, tab]);
  const totals = useMemo(() => {
    if (!data) return {};
    const t = Object.fromEntries(TABS.map((t) => [t.key, (data[t.key] || []).length]));
    t.site_content = siteContent
      ? (siteContent.logos?.length || 0) + (siteContent.testimonials?.length || 0)
      : "—";
    t.clients = clients ? clients.length : "—";
    return t;
  }, [data, siteContent, clients]);

  if (!authed) {
    return (
      <div
        data-testid="admin-login"
        className="min-h-[70vh] bg-white flex items-center justify-center px-5"
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            load(key);
          }}
          className="w-full max-w-md bg-white border-2 border-[#1A1A1A] rounded-3xl p-8 nb-shadow-lg"
        >
          <LockKeyhole size={30} strokeWidth={2.5} className="text-[#FF007F]" />
          <h1 className="mt-4 font-display font-black text-3xl tracking-tighter">
            Admin
          </h1>
          <p className="mt-2 text-sm text-[#4A4A4A] font-medium">
            Enter the admin key to view every enquiry, lead, checklist and
            early-access signup in one place.
          </p>
          <input
            type="password"
            value={key}
            onChange={(e) => setKey(e.target.value)}
            placeholder="Admin key"
            data-testid="admin-key-input"
            className="mt-5 w-full border-2 border-[#1A1A1A] rounded-xl px-4 py-3 font-semibold focus:outline-none focus:ring-4 focus:ring-[#FFD700]"
          />
          <button
            type="submit"
            disabled={loading || !key}
            data-testid="admin-submit-btn"
            className="mt-4 inline-flex items-center justify-center gap-2 w-full bg-[#1A1A1A] text-white border-2 border-[#1A1A1A] rounded-full px-5 py-3 font-bold uppercase tracking-wide nb-shadow-sm nb-press disabled:opacity-70"
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : <ArrowUpRight size={16} strokeWidth={2.5} />}
            Sign in
          </button>
        </form>
      </div>
    );
  }

  return (
    <div data-testid="admin-page" className="bg-[#F8F9FA] min-h-[80vh]">
      <div className="mx-auto max-w-7xl px-5 md:px-10 lg:px-12 py-10 md:py-14">
        <div className="flex items-end justify-between gap-6 flex-wrap">
          <div>
            <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#FF007F] font-bold">
              Admin · Inbox
            </p>
            <h1 className="mt-2 font-display font-black text-4xl md:text-5xl tracking-tighter">
              Everything in one place.
            </h1>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => load(key)}
              disabled={loading}
              data-testid="admin-refresh-btn"
              className="inline-flex items-center gap-2 bg-white text-[#1A1A1A] border-2 border-[#1A1A1A] rounded-full px-4 py-2 text-xs font-bold uppercase tracking-widest nb-shadow-sm nb-press disabled:opacity-70"
            >
              {loading ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} strokeWidth={2.5} />}
              Refresh
            </button>
            <button
              type="button"
              onClick={signOut}
              data-testid="admin-logout-btn"
              className="inline-flex items-center gap-2 bg-[#1A1A1A] text-white border-2 border-[#1A1A1A] rounded-full px-4 py-2 text-xs font-bold uppercase tracking-widest nb-shadow-sm nb-press"
            >
              <LogOut size={13} strokeWidth={2.5} /> Sign out
            </button>
          </div>
        </div>

        {/* Pipeline stats */}
        <PipelineStats data={data} />

        {/* Tabs */}
        <div className="mt-6 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              data-testid={`admin-tab-${t.key}`}
              className={`text-left rounded-2xl border-2 border-[#1A1A1A] p-5 nb-shadow-sm transition-transform ${
                tab === t.key ? "nb-shadow translate-x-[-2px] translate-y-[-2px]" : "nb-press"
              }`}
              style={{
                backgroundColor: tab === t.key ? t.color : "#FFFFFF",
                color: tab === t.key && t.key !== "early_access" ? "#FFFFFF" : "#1A1A1A",
              }}
            >
              <p className="font-mono text-[10px] uppercase tracking-widest opacity-80">
                {t.label}
              </p>
              <p className="mt-1 font-display font-black text-3xl tracking-tighter">
                {totals[t.key] ?? "—"}
              </p>
            </button>
          ))}
        </div>

        {/* Site content manager */}
        {tab === "site_content" && (
          <ContentManager adminKey={key} content={siteContent} onChanged={loadSiteContent} />
        )}

        {/* Clients manager */}
        {tab === "clients" && (
          <ClientsManager adminKey={key} clients={clients} onChanged={() => loadClients(key)} />
        )}

        {/* Toolbar + Table */}
        {tab !== "site_content" && tab !== "clients" && (
        <div className="mt-8 rounded-3xl bg-white border-2 border-[#1A1A1A] nb-shadow overflow-hidden">
          <div className="flex items-center justify-between p-5 border-b-2 border-[#1A1A1A] flex-wrap gap-3">
            <p className="font-mono text-[11px] uppercase tracking-widest text-[#4A4A4A]">
              {rows.length} {rows.length === 1 ? "entry" : "entries"} · most recent first
            </p>
            <button
              type="button"
              onClick={() =>
                downloadCSV(`tinkershift-${tab}-${Date.now()}.csv`, toCSV(rows))
              }
              disabled={!rows.length}
              data-testid={`admin-export-${tab}`}
              className="inline-flex items-center gap-2 bg-[#FFD700] text-[#1A1A1A] border-2 border-[#1A1A1A] rounded-full px-4 py-2 text-xs font-bold uppercase tracking-widest nb-shadow-sm nb-press disabled:opacity-70"
            >
              <Download size={13} strokeWidth={2.5} /> Export CSV
            </button>
          </div>
          <div className="divide-y divide-[#1A1A1A]/10 max-h-[70vh] overflow-y-auto">
            {rows.length === 0 && (
              <p className="p-8 text-center text-[#4A4A4A]">No entries yet.</p>
            )}
            {rows.map((r, i) => (
              <details
                key={r.id || i}
                data-testid={`admin-row-${tab}-${i}`}
                className="p-5 group"
              >
                <summary className="cursor-pointer flex items-start justify-between gap-4 flex-wrap">
                  <div className="min-w-0">
                    <p className="font-display font-black text-lg tracking-tight">
                      {r.full_name || r.name || r.email}
                      {r.service && (
                        <span className="ml-2 font-mono text-[10px] uppercase tracking-widest bg-[#FF007F] text-white px-2 py-0.5 rounded-full">
                          {r.service}
                        </span>
                      )}
                      {r.interest && (
                        <span className="ml-2 font-mono text-[10px] uppercase tracking-widest bg-[#0055FF] text-white px-2 py-0.5 rounded-full">
                          {r.interest}
                        </span>
                      )}
                    </p>
                    <p className="mt-1 text-sm text-[#4A4A4A]">
                      {[r.organisation, r.role, r.email].filter(Boolean).join(" · ")}
                    </p>
                  </div>
                  <div className="flex flex-col items-end gap-2 shrink-0">
                    <p className="font-mono text-[10px] uppercase tracking-widest text-[#4A4A4A]">
                      {r.created_at ? new Date(r.created_at).toLocaleString("en-AU") : ""}
                    </p>
                    <div className="flex items-center gap-1.5 flex-wrap justify-end">
                      {mailtoFor(tab, r) && (
                        <a
                          href={mailtoFor(tab, r)}
                          onClick={(e) => {
                            e.preventDefault();
                            window.location.href = mailtoFor(tab, r);
                          }}
                          data-testid={`reply-btn-${tab}-${i}`}
                          title="Reply via email"
                          className="inline-flex items-center gap-1.5 bg-[#1A1A1A] text-white border-2 border-[#1A1A1A] rounded-full px-3 py-1 font-mono text-[10px] uppercase tracking-widest font-bold nb-press"
                        >
                          <Mail size={11} strokeWidth={2.5} /> Reply
                        </a>
                      )}
                      {PIPELINE_TABS.has(tab) &&
                        STATUSES.map((s) => {
                          const active = (r.status || "new") === s.key;
                          return (
                            <button
                              key={s.key}
                              type="button"
                              disabled={statusBusy === `${tab}:${r.id}`}
                              onClick={(e) => {
                                e.preventDefault();
                                if (!active) updateStatus(tab, r.id, s.key);
                              }}
                              data-testid={`status-${s.key}-${tab}-${i}`}
                              className={`rounded-full border-2 border-[#1A1A1A] px-3 py-1 font-mono text-[10px] uppercase tracking-widest font-bold transition-colors disabled:opacity-60 ${
                                active ? "" : "bg-white text-[#1A1A1A]/50 hover:text-[#1A1A1A]"
                              }`}
                              style={
                                active
                                  ? { backgroundColor: s.color, color: s.darkText ? "#1A1A1A" : "#FFFFFF" }
                                  : undefined
                              }
                            >
                              {s.label}
                            </button>
                          );
                        })}
                    </div>
                  </div>
                </summary>
                <pre className="mt-3 p-4 bg-[#F8F9FA] border-2 border-[#1A1A1A] rounded-xl text-xs overflow-x-auto whitespace-pre-wrap font-mono">
                  {JSON.stringify(r, null, 2)}
                </pre>
              </details>
            ))}
          </div>
        </div>
        )}
      </div>
    </div>
  );
}
